// "Who's using it, and when" — the CEO's usage & adoption design (visuals v2):
// period tabs drive an active-users dot matrix, the lane-capture rate, the
// estimates-written spark, a per-active-user bullet vs target, and the user
// activity leaderboard (idle/never users stay visible — that's the point).
// Below: the advisor-efficiency table (conversion quality) and the arrival
// heatmap.

import { Fragment } from "react"
import { ArrowUp, ArrowDown, SwitchVertical01 } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { ChapterSection, ChartCard, PillTabs, Highlight } from "../room-chrome"
import { CAT, INDUSTRY_CLOSE_RATE, RAMP, fmt$, fmt$full, median } from "../room-model"
import { heatColor, type AdvisorRow, type AdvisorSortKey, type HighlightAction, type HighlightFinding, type UsageSection, type UsagePeriodKey, type deriveHeatmap } from "../derive"

type Heatmap = ReturnType<typeof deriveHeatmap>

const PERIODS: { key: UsagePeriodKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "wtd", label: "Week to date" },
  { key: "mtd", label: "Month to date" },
  { key: "all", label: "All" },
]

const STATUS_META = {
  active: { dot: "#1D9E75", label: "Active" },
  quiet: { dot: "#EF9F27", label: "Quiet in period" },
  idle: { dot: "#EF9F27", label: "No activity 3+ business days" },
  never: { dot: "#E24B4A", label: "Enrolled, never active" },
} as const

const initials = (n: string) => n.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)
const fmtLastActive = (t: number | null) => {
  if (t == null) return "Never"
  const d = new Date(t)
  const today = new Date(); today.setHours(0, 0, 0, 0)
  if (t >= today.getTime()) return `Today · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
  if (t >= today.getTime() - 86400000) return `Yesterday · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export function PeopleChapter({
  first, usage, onUsagePeriod, highlight, onHighlight, advisorRows, maxSubmitted, advisorSort, onSort, onToggleAdvisor, advisorId,
  perAdvisorMonthlyGoals, defaultMonthlyGoal,
  heatmap, channelLabel, rangeLabel,
  memberMeta,
}: {
  first?: boolean
  usage: UsageSection
  onUsagePeriod: (p: UsagePeriodKey) => void
  highlight?: HighlightFinding
  onHighlight: (a: HighlightAction) => void
  advisorRows: AdvisorRow[]
  maxSubmitted: number
  advisorSort: { key: AdvisorSortKey; desc: boolean }
  onSort: (key: AdvisorSortKey) => void
  onToggleAdvisor: (id: string) => void
  advisorId: string
  // Per-advisor monthly goal (override, else team ÷ headcount) — the manager's
  // reporting view of the goals they set (#30).
  perAdvisorMonthlyGoals: Record<string, number>
  defaultMonthlyGoal: number | null
  heatmap: Heatmap
  channelLabel: string
  rangeLabel: string
  /** id → role + department — the user's identity line under their name. */
  memberMeta?: Map<string, { role: string; department: string | null }>
}) {
  const sortBtn = (key: AdvisorSortKey, label: string) => (
    <button onClick={() => onSort(key)}
      className={cx("inline-flex items-center gap-1 hover:text-zinc-800 transition-colors", advisorSort.key === key ? "text-zinc-800" : "")}>
      {label}
      {advisorSort.key === key
        ? (advisorSort.desc ? <ArrowDown className="size-3" aria-hidden /> : <ArrowUp className="size-3" aria-hidden />)
        : <SwitchVertical01 className="size-3 opacity-40" aria-hidden />}
    </button>
  )

  // Busiest arrival window (top heatmap cell) — the one-line staffing takeaway.
  const busiest = (() => {
    let best = { v: 0, r: 0, c: 0 }
    heatmap.grid.forEach((row, r) => row.forEach((v, c) => { if (v > best.v) best = { v, r, c } }))
    if (best.v < 2) return null
    const h = heatmap.hours[best.c]
    return `${["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][best.r]} ${h > 12 ? h - 12 : h}${h >= 12 ? "p" : "a"}`
  })()

  const maxSpark = Math.max(...usage.spark.map(s => s.v), 1)
  const hotIdx = usage.spark.reduce((best, s, i, arr) => (s.v > arr[best].v ? i : best), 0)
  const topVal = Math.max(...usage.users.map(u => u.value), 1)
  // Median tick on every bar track (the CEO's bullet-target element) — makes
  // "2.4× the median" visible instead of stated.
  const medianVal = median(usage.users.filter(u => u.value > 0).map(u => u.value))
  const medianPct = medianVal > 0 ? Math.min((medianVal / topVal) * 100, 98) : null
  const bulletMax = usage.target != null ? usage.target * 1.5 : Math.max(usage.perUser * 1.25, 1)
  const targetDiff = usage.target != null ? usage.perUser - usage.target : null

  return (
    <ChapterSection id="people" eyebrow="Usage & adoption" title="Who's using it, and when" audience="For the lane" first={first}>
      {/* Period tabs — this section reports on its own clock (today / WTD / MTD / all) */}
      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        <PillTabs ariaLabel="Usage reporting period" items={PERIODS} active={usage.period} onSelect={onUsagePeriod} />
        <span className="ml-auto text-[11.5px] text-[#8B897E]">
          {usage.period === "all" ? `Program to date · since ${usage.periodStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : `since ${usage.periodStart.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}`}
        </span>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-3">
        {/* 1 · Active users — dot matrix */}
        <div className="animate-element rounded-2xl border border-[#ECEBE6] bg-white p-4 flex flex-col">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8B897E] mb-2">Active users</p>
          <p className="text-2xl font-bold tabular-nums leading-none text-zinc-900">
            {usage.activeCount} <span className="text-sm font-normal text-[#8B897E]">/ {usage.enrolled}</span>
          </p>
          <p className="text-[11px] text-[#8B897E] mt-1.5">every dot is one enrolled lane user</p>
          <div className="mt-auto pt-3">
            <div className="grid grid-cols-6 gap-1.5 max-w-[150px]" role="img" aria-label="Enrolled users by activity status">
              {usage.users.map(u => (
                <span key={u.id} title={`${u.name} — ${STATUS_META[u.status].label}`}
                  className="aspect-square rounded-full"
                  style={u.status === "active"
                    ? { background: "#1D9E75" }
                    : { border: `2px solid ${STATUS_META[u.status].dot}` }} />
              ))}
            </div>
            <div className="flex gap-2.5 mt-2 text-[10px] text-[#8B897E] flex-wrap">
              <span className="flex items-center gap-1"><i className="size-2 rounded-full inline-block" style={{ background: "#1D9E75" }} />Active</span>
              <span className="flex items-center gap-1"><i className="size-1.5 rounded-full inline-block border-2" style={{ borderColor: "#EF9F27" }} />Idle</span>
              <span className="flex items-center gap-1"><i className="size-1.5 rounded-full inline-block border-2" style={{ borderColor: "#E24B4A" }} />Never</span>
            </div>
          </div>
        </div>

        {/* 2 · Lane capture — needs the dealer-provided lane volume */}
        <div className="animate-element rounded-2xl border border-[#ECEBE6] bg-white p-4 flex flex-col">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8B897E] mb-2">Lane capture</p>
          {usage.capturePct != null && usage.laneROs != null ? (
            <>
              <p className="text-2xl font-bold tabular-nums leading-none text-zinc-900">{usage.capturePct}%</p>
              <p className="text-[11px] text-[#8B897E] mt-1.5">{usage.evaluated} of ~{usage.laneROs} lane vehicles evaluated</p>
              <div className="mt-auto pt-3">
                <div className="h-3 rounded-full bg-[#F1F0EB] overflow-hidden">
                  <div className="h-full rounded-full bg-[#1D9E75] transition-all duration-300" style={{ width: `${usage.capturePct}%` }} />
                </div>
                <div className="flex justify-between text-[10px] text-[#8B897E] tabular-nums mt-1"><span>0</span><span>~{usage.laneROs}</span></div>
                {usage.laneROs - usage.evaluated > 0 && (
                  <p className="text-[11px] font-semibold text-amber-700 mt-1.5">{usage.laneROs - usage.evaluated} vehicles passed through unevaluated</p>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold tabular-nums leading-none text-zinc-900">{usage.evaluated}</p>
              <p className="text-[11px] text-[#8B897E] mt-1.5">vehicles evaluated in period</p>
              <p className="text-[11px] text-[#8B897E] mt-auto pt-3">Set your average daily lane volume to see the capture rate.</p>
            </>
          )}
        </div>

        {/* 3 · Estimates written — spark */}
        <div className="animate-element rounded-2xl border border-[#ECEBE6] bg-white p-4 flex flex-col">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8B897E] mb-2">Estimates written</p>
          <p className="text-2xl font-bold tabular-nums leading-none text-zinc-900">{usage.estimates}</p>
          <p className="text-[11px] text-[#8B897E] mt-1.5">{fmt$full(usage.identified)} opportunity identified</p>
          <div className="mt-auto pt-3">
            <div className="flex items-end gap-1 h-12" role="img" aria-label="Estimate volume within the selected period">
              {usage.spark.map((s, i) => (
                <div key={i} title={`${s.v} estimate${s.v !== 1 ? "s" : ""}`}
                  className="flex-1 rounded-t-[3px]"
                  style={{ height: `${Math.max((s.v / maxSpark) * 100, 6)}%`, background: i === hotIdx && s.v > 0 ? "#1D9E75" : "#9FE1CB" }} />
              ))}
            </div>
            <div className="flex gap-1 mt-1">
              {usage.spark.map((s, i) => (
                <span key={i} className="flex-1 text-center text-[8.5px] text-[#8B897E] tabular-nums truncate">{s.label}</span>
              ))}
            </div>
          </div>
        </div>

        {/* 4 · Est. per active user vs target — bullet */}
        <div className="animate-element rounded-2xl border border-[#ECEBE6] bg-white p-4 flex flex-col">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8B897E] mb-2">Est. per active user</p>
          <p className="text-2xl font-bold tabular-nums leading-none text-zinc-900">{usage.perUser.toFixed(1)}</p>
          <p className="text-[11px] text-[#8B897E] mt-1.5">{usage.target != null ? `target: ${usage.target.toFixed(1)} per user` : "no period target configured"}</p>
          <div className="mt-auto pt-3">
            <div className="relative h-[22px] rounded-md bg-[#F1F0EB]">
              <div className="absolute top-[5px] left-0 h-3 rounded-r-[3px] bg-[#1D9E75] transition-all duration-300" style={{ width: `${Math.min((usage.perUser / bulletMax) * 100, 100)}%` }} />
              {usage.target != null && (
                <div className="absolute -top-[3px] w-[2.5px] h-[28px] rounded bg-zinc-900" style={{ left: `${Math.min((usage.target / bulletMax) * 100, 98)}%` }} />
              )}
            </div>
            <div className="flex justify-between text-[10px] text-[#8B897E] tabular-nums mt-1.5"><span>0</span><span>{bulletMax.toFixed(1)}</span></div>
            {targetDiff != null && (
              <p className={cx("text-[11px] font-semibold mt-1", targetDiff >= 0 ? "text-[#0F6E56]" : "text-amber-700")}>
                {targetDiff >= 0 ? "+" : ""}{targetDiff.toFixed(1)} {targetDiff >= 0 ? "ahead of" : "behind"} target
              </p>
            )}
          </div>
        </div>
      </div>

      {/* User activity leaderboard */}
      <div className="animate-element rounded-2xl border border-[#ECEBE6] bg-white overflow-hidden mb-3">
        <div className="px-6 pt-5 pb-3">
          <p className="text-sm font-semibold text-zinc-900">User activity</p>
          <p className="text-xs text-[#8B897E] mt-0.5 mb-2">Ranked by $ identified in the period — click a user to filter the room · tick = median user · idle users stay on the board</p>
          {highlight && <Highlight text={highlight.text} onClick={highlight.action ? () => onHighlight(highlight.action!) : undefined} />}
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[680px] px-6 pb-4">
            <div className="grid grid-cols-[34px_minmax(130px,1.1fr)_2fr_52px_52px_88px_104px] gap-2.5 items-center py-1.5 text-[11px] font-semibold text-[#8B897E] uppercase tracking-wider">
              <span /><span>User</span><span>$ identified</span><span className="text-right">Veh</span><span className="text-right">Est</span><span className="text-right">Value</span><span className="text-right">Last active</span>
            </div>
            {usage.users.map(u => {
              const idle = u.est === 0 && u.veh === 0
              const selected = advisorId === u.id
              return (
                <button key={u.id} onClick={() => onToggleAdvisor(u.id)}
                  className={cx(
                    "w-full grid grid-cols-[34px_minmax(130px,1.1fr)_2fr_52px_52px_88px_104px] gap-2.5 items-center py-2 border-t border-[#ECEBE6] text-left transition-colors",
                    selected ? "bg-[#E8F8F2]" : "hover:bg-[#F7F6F1]",
                    (idle && !selected) || (advisorId && !selected) ? "opacity-60" : "",
                  )}>
                  <span className="relative size-8 rounded-full grid place-items-center text-[11px] font-bold shrink-0"
                    style={{ background: idle ? "#F1F0EB" : "#E8F8F2", color: idle ? "#71717A" : "#085041" }}>
                    {initials(u.name)}
                    <span className="absolute -right-px -bottom-px size-[9px] rounded-full border-2 border-white" style={{ background: STATUS_META[u.status].dot }} />
                  </span>
                  <span className="min-w-0">
                    <span className={cx("block text-[13px] truncate", idle ? "font-medium text-[#6E6C64]" : "font-semibold text-zinc-900")}>{u.name}</span>
                    {/* Who they are: role, then department (Anthony, Jul 28). */}
                    {memberMeta?.get(u.id) && (
                      <span className="block text-[10.5px] text-[#8B897E] truncate leading-tight">
                        {memberMeta.get(u.id)!.role}
                        {memberMeta.get(u.id)!.department
                          ? ` · ${memberMeta.get(u.id)!.department}`
                          : ""}
                      </span>
                    )}
                  </span>
                  <span className="relative h-2.5 rounded-full bg-[#F1F0EB] overflow-visible block">
                    <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${(u.value / topVal) * 100}%`, background: idle ? "#DDDBD2" : "#1D9E75" }} />
                    {medianPct != null && (
                      <span className="absolute -top-[3px] w-[2px] h-[16px] rounded bg-zinc-900/70" style={{ left: `${medianPct}%` }} aria-hidden />
                    )}
                  </span>
                  <span className={cx("text-right text-[12.5px] tabular-nums", idle ? "text-[#8B897E]" : "font-bold text-zinc-800")}>{u.veh}</span>
                  <span className={cx("text-right text-[12.5px] tabular-nums", idle ? "text-[#8B897E]" : "font-bold text-zinc-800")}>{u.est}</span>
                  <span className={cx("text-right text-[12.5px] tabular-nums", idle ? "text-[#8B897E]" : "font-bold text-zinc-900")}>{u.value ? fmt$(u.value) : "—"}</span>
                  <span className="text-right text-[11px] text-[#8B897E] whitespace-nowrap">{fmtLastActive(u.lastActive)}</span>
                </button>
              )
            })}
            <div className="flex gap-3.5 pt-3 text-[11px] text-[#8B897E] flex-wrap">
              <span className="flex items-center gap-1.5"><i className="size-2 rounded-full inline-block" style={{ background: "#1D9E75" }} />Active</span>
              <span className="flex items-center gap-1.5"><i className="size-2 rounded-full inline-block" style={{ background: "#EF9F27" }} />No activity in 3+ business days</span>
              <span className="flex items-center gap-1.5"><i className="size-2 rounded-full inline-block" style={{ background: "#E24B4A" }} />Enrolled, never active</span>
            </div>
            {(usage.uplift > 500 || usage.bestDay) && (
              <p className="text-[12px] text-[#6E6C64] pt-2.5 mt-2.5 border-t border-[#ECEBE6]">
                {usage.uplift > 500 && usage.topName && (
                  <>If everyone identified like <strong className="text-zinc-800">{usage.topName}</strong> this period, that's ≈ <strong className="text-[#0F6E56] tabular-nums">+{fmt$(usage.uplift)}</strong> more found.</>
                )}
                {usage.uplift > 500 && usage.bestDay && " · "}
                {usage.bestDay && (
                  <>Best day: <strong className="text-zinc-800">{usage.bestDay.label}</strong> — {usage.bestDay.count} estimates, <span className="tabular-nums font-semibold text-zinc-800">{fmt$(usage.bestDay.value)}</span>.</>
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {/* Advisor efficiency table — conversion quality per person */}
        <div className="animate-element rounded-2xl border border-[#ECEBE6] bg-white overflow-hidden">
          <div className="px-6 pt-5 pb-3">
            <p className="text-sm font-semibold text-zinc-900">Advisor efficiency</p>
            <p className="text-xs text-[#8B897E] mt-0.5">Click an advisor to filter the room · {channelLabel} · {rangeLabel}</p>
          </div>
          {advisorRows.length === 0 ? (
            <p className="px-6 pb-6 text-sm text-[#8B897E]">No advisor attribution on these estimates yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                <div className="flex items-center px-6 py-2 border-y border-[#ECEBE6] bg-[#F1F0EB]/60 text-[11px] font-semibold text-[#8B897E] uppercase tracking-wider">
                  <span className="flex-1">Advisor</span>
                  <span className="w-32">{sortBtn("submitted", "Submitted")}</span>
                  <span className="w-24 text-right">Monthly goal</span>
                  <span className="w-24 text-right">{sortBtn("converted", "Converted")}</span>
                  <span className="w-20 text-right">{sortBtn("closeRate", "Close")}</span>
                  <span className="w-20 text-right">{sortBtn("declineRate", "Decline")}</span>
                  <span className="w-24 text-right">{sortBtn("identified", "Found $")}</span>
                  <span className="w-28 text-right">{sortBtn("medDays", "Med. close")}</span>
                </div>
                {advisorRows.map(r => {
                  const selected = advisorId === r.id
                  return (
                    <button key={r.id} onClick={() => onToggleAdvisor(r.id)}
                      className={cx(
                        "w-full flex items-center px-6 py-3 border-b border-[#ECEBE6] last:border-b-0 text-left transition-colors",
                        selected ? "bg-[#E8F8F2]" : "hover:bg-[#F7F6F1]",
                        advisorId && !selected ? "opacity-45" : "",
                      )}>
                      <span className="flex-1 flex items-center gap-2 min-w-0">
                        <span className={cx("size-1.5 rounded-full shrink-0", selected ? "bg-[#0F6E56]" : "bg-transparent")} />
                        <span className="text-sm font-medium text-zinc-900 truncate">{r.name}</span>
                      </span>
                      <span className="w-32 flex items-center gap-2">
                        <span className="flex-1 h-1.5 rounded-full bg-[#F1F0EB] overflow-hidden">
                          <span className="block h-full rounded-full" style={{ width: `${(r.submitted / maxSubmitted) * 100}%`, background: CAT.light }} />
                        </span>
                        <span className="text-xs text-[#6E6C64] tabular-nums w-6 text-right">{r.submitted}</span>
                      </span>
                      <span className="w-24 text-right text-sm tabular-nums">
                        {(() => {
                          const goal = perAdvisorMonthlyGoals[r.id] ?? defaultMonthlyGoal
                          return goal != null ? (
                            <span className="text-[#6E6C64]">
                              {goal}
                              <span className="text-[#8B897E] text-[11px]">/mo</span>
                            </span>
                          ) : (
                            <span className="text-[#8B897E]">—</span>
                          )
                        })()}
                      </span>
                      <span className="w-24 text-right text-sm text-zinc-700 tabular-nums">{r.converted}</span>
                      <span className={cx("w-20 text-right text-sm font-medium tabular-nums", r.closeRate >= INDUSTRY_CLOSE_RATE ? "text-[#0F6E56]" : "text-zinc-700")}>{r.closeRate}%</span>
                      <span className={cx("w-20 text-right text-sm tabular-nums", r.declineRate >= 25 ? "text-[#A32D2D] font-medium" : "text-[#6E6C64]")}>{r.declineRate}%</span>
                      <span className="w-24 text-right text-sm font-semibold text-zinc-900 tabular-nums">{fmt$(r.identified)}</span>
                      <span className="w-28 text-right text-sm text-[#6E6C64] tabular-nums">{r.medDays > 0 ? `${r.medDays.toFixed(1)}d` : "—"}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* When opportunity arrives */}
        <ChartCard title="When opportunity arrives" sub={`Estimates by weekday and hour — staff the lane where the green is${busiest ? ` · busiest: ${busiest}` : ""}`}>
          <div className="mt-1 max-w-2xl">
            <div className="grid gap-[3px]" style={{ gridTemplateColumns: `2rem repeat(${heatmap.hours.length}, minmax(0,1fr))` }}>
              <span />
              {heatmap.hours.map(h => (
                <span key={h} className="text-[9px] text-[#8B897E] text-center tabular-nums">{h > 12 ? h - 12 : h}{h >= 12 ? "p" : "a"}</span>
              ))}
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, r) => (
                <Fragment key={day}>
                  <span className="text-[10px] text-[#8B897E] flex items-center">{day}</span>
                  {heatmap.hours.map((h, c) => {
                    const v = heatmap.grid[r][c]
                    return (
                      <span key={`${day}-${h}`} title={`${day} ${h}:00 — ${v} estimate${v !== 1 ? "s" : ""}`}
                        className="aspect-square rounded-[3px]" style={{ background: heatColor(v, heatmap.max) }} />
                    )
                  })}
                </Fragment>
              ))}
            </div>
            <div className="flex items-center justify-end gap-1.5 mt-3 text-[10px] text-[#8B897E]">
              less
              {["#F1F0EB", "#DCEFE5", RAMP[0], RAMP[1], RAMP[3]].map(c => (
                <span key={c} className="size-2.5 rounded-[2px]" style={{ background: c }} />
              ))}
              more
            </div>
          </div>
        </ChartCard>
      </div>
    </ChapterSection>
  )
}
